import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/auth/auth-store';
import { forgotPassword, loginByCode } from '@/auth/auth-api';
import { isBlank } from '@/auth/email-validation';
import { safeLogAuthError } from '@/auth/log-helpers';
import { track } from '@/shared/lib/analytics';

/**
 * Reset Password — Access Code entry (Phase 3a of PROD-890).
 *
 * Flutter source:
 *   - `reset_password_access_code/presentation/pages/reset_password_access_code_screen.dart`
 *   - `…/widgets/body_widgets.dart` (code field + Resend code)
 *   - `…/widgets/footer_widgets.dart` (Next + Log in)
 *   - `…/widgets/header_widgets.dart` ("Reset password email was sent" / subtitle)
 *   - `…/bloc/reset_password_access_code_bloc.dart` (SignupWithCodeEvent + ResendCodeCallEvent)
 *
 * Entry: `navigate('/reset-password-access-code', { state: { email } })`
 * from Phase 2's forgot-password success state.
 *
 * Flow:
 *   1. Email read from `location.state.email` (read-only)
 *   2. User enters 6-digit code
 *   3. Resend link → `forgotPassword(email)` (reuses Phase 2 endpoint)
 *   4. Next button → `loginByCode({email, code, isResetPassword: true})`
 *      → response carries `passwordResetToken`
 *   5. On success → navigate to `/reset-password-new-password`
 *      carrying `{email, passwordResetToken}`
 */
export function ResetPasswordAccessCodePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);

  // Email arrives via React Router state from /forgot-password.
  // If a user lands here without state (direct URL hit), bounce them back.
  const incomingState = location.state as { email?: string } | undefined;
  const email = incomingState?.email ?? '';

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to="/" replace />;
  }

  if (isBlank(email)) {
    // No email in state → user must restart the flow at /forgot-password.
    return <Navigate to="/forgot-password" replace />;
  }

  // Flutter `reset_password_access_code_bloc.dart:50-54` — button enabled
  // when both email and code are non-blank. Email is always non-blank
  // here (guarded above); code must also be non-blank.
  const canSubmit = !isBlank(code) && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const response = await loginByCode({
        email,
        code,
        isResetPassword: true,
      });
      if (!response.passwordResetToken) {
        // Backend accepted the call but didn't issue a token — treat as
        // invalid code per Flutter's behavior (signup_by_code_response_model
        // ships nullable token).
        toast.error('Failed to signup');
        return;
      }
      // Flutter `reset_password_access_code_bloc.dart:40-45`: fire on
      // successful code verification.
      track('Password Reset Code Verified', { email });
      navigate('/reset-password-new-password', {
        state: {
          email: response.emailAddress ?? email,
          passwordResetToken: response.passwordResetToken,
        },
      });
    } catch (err) {
      // Flutter `reset_password_access_code_bloc.dart:27-36`: fire on failure.
      track('Password Reset Failed', {
        email,
        message: 'Reset Password Access Code Verification failed',
        error: err instanceof Error ? err.message : String(err),
      });
      safeLogAuthError('Reset password — access code', err);
      toast.error('Failed to signup');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onResend() {
    if (isResending) return;
    setIsResending(true);
    try {
      await forgotPassword(email);
      toast.success(`Reset code sent to ${email}`);
    } catch (err) {
      safeLogAuthError('Reset password — resend code', err);
      toast.error('Failed to send Reset code');
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <form
        onSubmit={onSubmit}
        className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4"
        aria-label="Enter your reset code"
      >
        <AuthBrandHeading
          title="Reset password email was sent"
          subtitle="Enter the reset code we just emailed you"
        />

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="reset-access-code"
            className="text-xs font-medium text-text-secondary"
          >
            Reset code
          </Label>
          <Input
            id="reset-access-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Enter reset code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <button
          type="button"
          onClick={onResend}
          disabled={isResending}
          className="self-start text-sm font-medium text-brand-primary hover:underline disabled:opacity-60"
        >
          {isResending ? 'Resending…' : 'Resend code'}
        </button>

        <Button type="submit" disabled={!canSubmit} className="mt-2">
          {isSubmitting ? 'Verifying…' : 'Next'}
        </Button>

        <Link
          to="/login"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Log in
        </Link>
      </form>
    </div>
  );
}

export default ResetPasswordAccessCodePage;
